<template>
  <div>
    <listing-controls
      v-if="enableFiltering || enableSorting"
      :listing="listing"
      :enable-filtering="enableFiltering"
      :enable-sorting="enableSorting"
      :allow-custom-filter="enableFiltering && !isClient"
      :sorters="orderSorters"
      :active-sort="activeSort"
      class="has-margin-bottom-100"
      @refresh="ordersMethods.reloadData"
      @sort="ordersMethods.onSort"
    >
      <!-- Search input -->
      <template #search>
        <b-input
          v-focus="$store.getters['ui/isTabletPlus']"
          :value="listing.search.value"
          :icon-right="listing.search.value ? 'close-circle' : null"
          :placeholder="$t('_placeholder.orders_quick_search')"
          :icon-right-clickable="true"
          @icon-right-click="ordersMethods.doQuickSearch()"
          @input="ordersMethods.debounceSearch"
        />
      </template>

      <!-- Refresh Control -->
      <template v-if="listing.activeFilters.value.length" #controlRefresh>
        <b-dropdown
          :mobile-modal="false"
          position="is-bottom-left"
          @click.native.stop
          @click.prevent
        >
          <b-button slot="trigger" icon-left="ellipsis-v" />
          <!-- Add segment -->
          <add-segment-message
            :api-filters="apiFilters"
            :api-props="segmentApiProps"
            :segment-type="segmentType"
          />
        </b-dropdown>
      </template>
    </listing-controls>
    <orders-table-view />
    <u-pagination
      :current="listing.page.value"
      :per-page="listing.limit.value"
      :total="listing.total.value"
      :default-limit="defaults.limit"
      :visible-total="listing.data.value.length"
      @change-page="ordersMethods.onPageChange"
      @change-per-page="ordersMethods.onLimitChange"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, toRef } from "vue";
import { useOrdersListing } from "@/composables/orders";
import { useContext } from "@/composables/context";
import { DefaultOrderSorters } from "@/data/sorters/order";
import { DataModules } from "@/store/modules/data/modules";
import { SegmentTypes } from "@/data/enums/segments";
import type { PropType } from "vue";
import type { ISorter } from "@/models/tables";
import type { IClient } from "@upmind-automation/types";

export default defineComponent({
  name: "OrdersListing",
  components: {
    "listing-controls": () => import("@/components/base/ListingControls.vue"),
    "orders-table-view": () =>
      import("@/components/app/global/orders/ordersTableView.vue"),
    "add-segment-message": () =>
      import("@/components/app/admin/segments/addSegmentMessage.vue")
  },
  props: {
    clientId: { type: String as PropType<IClient["id"]>, default: null },
    availableSorters: {
      type: Function as PropType<(vm?: any) => ISorter[]>,
      default: DefaultOrderSorters
    },
    manualFilters: {
      type: Function as PropType<
        () => Record<string, string | number | boolean>
      >,
      default: () => ({})
    }
  },
  setup(props) {
    const ctx = useContext();
    const orderSorters = ref<ISorter[]>(props.availableSorters());
    const providerManualFilters = computed(() => props.manualFilters());

    const {
      listing,
      activeSort,
      defaults,
      apiFilters,
      ordersProviderMethodsObj: ordersMethods
    } = useOrdersListing({
      id: "OP1",
      clientId: toRef(props, "clientId"),
      availableSorters: orderSorters,
      manualFilters: providerManualFilters,
      provide: true,
      autoLoad: true
    });

    return {
      listing,
      activeSort,
      defaults,
      apiFilters,
      ordersMethods,
      orderSorters,
      isClient: ctx.isClient,
      enableFiltering: true,
      enableSorting: true,
      segmentType: SegmentTypes.ORDER
    };
  },
  computed: {
    segmentApiProps() {
      return {
        with: this.$store.getters[`data/${DataModules.ORDERS}/withParam`]
      };
    }
  }
});
</script>
